import { Plural, Trans } from '@lingui/react/macro';

import { useOpportunityOverviewKPIMetrics } from '@/api/opportunity';
import { KpiMetric, KpiMetricGroup } from '@/components/kpi';
import { usePageFilterContext } from '@/components/page';
import { numberFormat } from '@/helper/numberFormatter';

export const OpportunitiesOverviewKPIMetrics = () => {
  const {
    pageFilters: { startDate, endDate, audience },
  } = usePageFilterContext();
  const { opportunityOverviewKPIMetrics, isLoading, error } = useOpportunityOverviewKPIMetrics(
    startDate,
    endDate,
    audience,
  );

  return (
    <KpiMetricGroup>
      <KpiMetric
        label={<Trans>Days to Close</Trans>}
        value={
          opportunityOverviewKPIMetrics &&
          numberFormat(opportunityOverviewKPIMetrics?.averageDaysToClose, { precision: 0 })
        }
        infoPopover={{
          body: (
            <Trans>
              Average number of days your closed won opportunities remained open from create to
              close date.
            </Trans>
          ),
        }}
        isLoading={isLoading}
        error={error}
      />
      <KpiMetric
        label={<Trans>Average Contract Value</Trans>}
        value={
          opportunityOverviewKPIMetrics &&
          numberFormat(opportunityOverviewKPIMetrics?.averageContractValue, {
            isCurrency: true,
            precision: 0,
          })
        }
        infoPopover={{
          body: (
            <Trans>
              Average value of all closed won opportunities within the selected date range.
            </Trans>
          ),
        }}
        isLoading={isLoading}
        error={error}
      />
      <KpiMetric
        label={<Trans>Opps to Closed Won</Trans>}
        value={
          opportunityOverviewKPIMetrics &&
          (!opportunityOverviewKPIMetrics.closedWonCount
            ? 0
            : numberFormat(opportunityOverviewKPIMetrics?.pipelineToClosedWonRatio, {
                precision: 1,
              }))
        }
        infoPopover={{
          body: (
            <Trans>
              Ratio of opportunities created to opportunities closed won within the selected date
              range.
            </Trans>
          ),
        }}
        isLoading={isLoading}
        error={error}
      />
      <KpiMetric
        label={<Trans>Win Rate</Trans>}
        value={
          opportunityOverviewKPIMetrics &&
          numberFormat(opportunityOverviewKPIMetrics.winRate, { isPercent: true, precision: 1 })
        }
        caption={
          opportunityOverviewKPIMetrics?.winRate && (
            <span>
              <Trans>
                {opportunityOverviewKPIMetrics?.closedWonCount} Closed Won /{' '}
                <Plural
                  value={
                    opportunityOverviewKPIMetrics &&
                    numberFormat(
                      opportunityOverviewKPIMetrics.closedWonCount +
                        opportunityOverviewKPIMetrics.closedLostOpportunityCount,
                    )
                  }
                  one="# Closed Opp"
                  other="# Closed Opps"
                />
              </Trans>
            </span>
          )
        }
        infoPopover={{
          body: (
            <Trans>
              Percentage of closed opportunities that were won within the selected date range.
            </Trans>
          ),
        }}
        isLoading={isLoading}
        error={error}
      />
      <KpiMetric
        label={<Trans>Pipeline Opportunities</Trans>}
        value={
          opportunityOverviewKPIMetrics &&
          numberFormat(opportunityOverviewKPIMetrics?.pipelineAmount, {
            isCurrency: true,
            precision: 0,
          })
        }
        caption={
          opportunityOverviewKPIMetrics?.pipelineCount && (
            <Plural
              value={
                opportunityOverviewKPIMetrics &&
                numberFormat(opportunityOverviewKPIMetrics?.pipelineCount)
              }
              one="# Opportunity"
              other="# Opportunities"
            />
          )
        }
        infoPopover={{
          body: (
            <Trans>
              Opportunities with a create date within the dates selected and their total potential
              revenue amount.
            </Trans>
          ),
        }}
        isLoading={isLoading}
        error={error}
      />
      <KpiMetric
        label={<Trans>Closed Won Opportunities</Trans>}
        value={
          opportunityOverviewKPIMetrics &&
          numberFormat(opportunityOverviewKPIMetrics?.closedWonAmount, {
            isCurrency: true,
            precision: 0,
          })
        }
        caption={
          opportunityOverviewKPIMetrics?.closedWonCount && (
            <Plural
              value={
                opportunityOverviewKPIMetrics &&
                numberFormat(opportunityOverviewKPIMetrics?.closedWonCount)
              }
              one="# Opportunity"
              other="# Opportunities"
            />
          )
        }
        infoPopover={{
          body: (
            <Trans>
              Opportunities with a close date within the dates selected and their total revenue
              amount.
            </Trans>
          ),
        }}
        isLoading={isLoading}
        error={error}
      />
    </KpiMetricGroup>
  );
};
